import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import {
  fetchPositions,
  createPosition,
  updatePosition,
  deletePosition,
  PORTAL_SECTIONS,
  STANDALONE_PRIVILEGES,
  getSectionPrivileges,
  getPrivilegeKey,
  CRUD_LABELS,
  formatPrivilegesGroupedBySection
} from '../../utils/positionsApi';
import { showSuccess, showError } from '../../components/CustomAlert';
import CustomConfirm from '../../components/CustomConfirm';
import { hasPermission } from '../../utils/permissions';

const Container = styled.div`
  padding: 24px 20px;
  background: #f8f9fa;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
`;

const AddButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;

  &:hover {
    background: rgb(33, 41, 90);
    transform: translateY(-1px);
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Th = styled.th`
  padding: 16px 20px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e9ecef;
  font-size: 0.95rem;
  &:nth-child(1) { width: 12%; }
  &:nth-child(2) { width: 32%; }
  &:nth-child(3) { width: 46%; }
  &:last-child {
    text-align: right;
    width: 30%;
  }
`;

const Td = styled.td`
  padding: 16px 20px;
  color: #495057;
  font-size: 0.9rem;
  border-bottom: 1px solid #e9ecef;
  &:last-child {
    text-align: right;
  }
`;

const Tr = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const EditButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: rgb(26, 35, 90);
  }
`;

const DeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: #c82333;
  }
`;

const PrivilegesList = styled.span`
  font-size: 0.85rem;
  color: #6c757d;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  width: 100%;
  max-width: 880px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #2c3e50;
  font-size: 1.4rem;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  &:hover {
    color: #495057;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 80px;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const PrivilegesSection = styled.div`
  margin-top: 20px;
`;

const PrivilegesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const PrivilegesInstruction = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #6c757d;
`;

const GlobalActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SectionSelectBtn = styled.button`
  padding: 6px 12px;
  font-size: 0.85rem;
  border-radius: 6px;
  border: 1px solid #3498db;
  background: white;
  color: #3498db;
  cursor: pointer;
  &:hover {
    background: #3498db;
    color: white;
  }
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 4px 0;
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const OtherPrivilegesCard = styled.div`
  grid-column: 1 / -1;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  padding: 12px 14px;
  background: #fafbfc;
`;

const OtherPrivilegesTitle = styled.div`
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
  margin-bottom: 10px;
`;

const OtherPrivilegesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
`;

const SectionCard = styled.div`
  border: 2px solid ${(p) => (p.selected ? '#3498db' : '#e9ecef')};
  border-radius: 10px;
  padding: 12px 14px;
  background: ${(p) => (p.selected ? 'rgba(52, 152, 219, 0.06)' : 'white')};
  transition: border-color 0.2s, background 0.2s;
`;

const SectionCardHeader = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
  margin-bottom: ${(p) => (p.selected ? '10px' : '0')};
`;

const CrudRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e9ecef;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  color: #495057;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const SaveButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  &:hover:not(:disabled) {
    background: rgb(33, 41, 90);
  }
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  &:hover {
    background: #5a6268;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6c757d;
`;

const Positions = () => {
  const { user } = useSelector((state) => state.auth);
  const canCreateStaff = hasPermission(user, 'staff_create');
  const canUpdateStaff = hasPermission(user, 'staff_update');
  const canDeleteStaff = hasPermission(user, 'staff_delete');

  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, position: null });
  const [form, setForm] = useState({
    name: '',
    description: '',
    privileges: []
  });

  const loadPositions = async () => {
    try {
      setLoading(true);
      const list = await fetchPositions();
      setPositions(list);
    } catch (err) {
      showError(err.message || 'Failed to load positions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPositions();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', description: '', privileges: [] });
    setShowModal(true);
  };

  // Normalize legacy privilege names when loading for edit
  const normalizePrivileges = (privileges) => {
    const legacy = { sessions_view: 'sessions_read', sessions_add: 'sessions_create' };
    return (privileges || []).reduce((acc, p) => {
      const normalized = legacy[p] || p;
      if (!acc.includes(normalized)) acc.push(normalized);
      return acc;
    }, []);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description || '',
      privileges: normalizePrivileges(p.privileges)
    });
    setShowModal(true);
  };

  const togglePrivilege = (priv) => {
    setForm((prev) => ({
      ...prev,
      privileges: prev.privileges.includes(priv)
        ? prev.privileges.filter((x) => x !== priv)
        : [...prev.privileges, priv]
    }));
  };

  const isSectionSelected = (sectionId) => {
    const privs = getSectionPrivileges(sectionId);
    return privs.some((p) => form.privileges.includes(p));
  };

  const hasSectionPrivilege = (sectionId, action) =>
    form.privileges.includes(getPrivilegeKey(sectionId, action));

  const selectAllPrivileges = () => {
    const sectionPrivs = PORTAL_SECTIONS.flatMap((s) => getSectionPrivileges(s.id));
    const standaloneKeys = STANDALONE_PRIVILEGES.map((s) => s.key);
    setForm((prev) => ({
      ...prev,
      privileges: [...new Set([...prev.privileges, ...sectionPrivs, ...standaloneKeys])]
    }));
  };

  const deselectAllPrivileges = () => {
    setForm((prev) => ({ ...prev, privileges: [] }));
  };

  const toggleSection = (sectionId, selected) => {
    const privs = getSectionPrivileges(sectionId);
    const readKey = getPrivilegeKey(sectionId, 'read');
    setForm((prev) => {
      const next = selected
        ? [...new Set([...prev.privileges, readKey, ...privs])]
        : prev.privileges.filter((p) => !privs.includes(p));
      return { ...prev, privileges: next };
    });
  };

  const toggleSectionCrud = (sectionId, action) => {
    const key = getPrivilegeKey(sectionId, action);
    togglePrivilege(key);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showError('Position name is required');
      return;
    }
    try {
      setSaving(true);
      if (editing) {
        await updatePosition(editing.id, form);
        showSuccess('Position updated');
      } else {
        await createPosition(form);
        showSuccess('Position created');
      }
      setShowModal(false);
      loadPositions();
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (p) => {
    setDeleteConfirm({ show: true, position: p });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.position) return;
    try {
      await deletePosition(deleteConfirm.position.id);
      showSuccess('Position deleted');
      setDeleteConfirm({ show: false, position: null });
      loadPositions();
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Delete failed');
    }
  };

  return (
    <Container>
      <Header>
        <Title>Positions</Title>
        {canCreateStaff && (
          <AddButton onClick={openAdd}>
            <FaPlus /> Add Position
          </AddButton>
        )}
      </Header>

      <TableContainer>
        {loading ? (
          <LoadingContainer>Loading...</LoadingContainer>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Description</Th>
                <Th>Privileges</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => (
                <Tr key={p.id}>
                  <Td>{p.name}</Td>
                  <Td>{p.description || '—'}</Td>
                  <Td>
                    <PrivilegesList>
                      {formatPrivilegesGroupedBySection(p.privileges) || '—'}
                    </PrivilegesList>
                  </Td>
                  <Td>
                    <ActionButtons>
                      {canUpdateStaff && (
                        <EditButton onClick={() => openEdit(p)}>
                          <FaEdit /> Edit
                        </EditButton>
                      )}
                      {canDeleteStaff && (
                        <DeleteButton onClick={() => handleDelete(p)}>
                          <FaTrash /> Delete
                        </DeleteButton>
                      )}
                    </ActionButtons>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
        {!loading && positions.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
            No positions yet. Add one to assign privileges to staff users.
          </div>
        )}
      </TableContainer>

      {showModal && (
        <ModalOverlay onClick={() => !saving && setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editing ? 'Edit Position' : 'Add Position'}</ModalTitle>
              <CloseBtn type="button" onClick={() => !saving && setShowModal(false)}>&times;</CloseBtn>
            </ModalHeader>
            <FormGroup>
              <Label>Position name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Receptionist"
              />
            </FormGroup>
            <FormGroup>
              <Label>Description (optional)</Label>
              <TextArea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Brief description"
              />
            </FormGroup>
            <PrivilegesSection>
              <PrivilegesHeader>
                <                PrivilegesInstruction>
                  Select sections and configure Read, Create, Update, Delete permissions for each section.
                </PrivilegesInstruction>
                <GlobalActions>
                  <SectionSelectBtn type="button" onClick={selectAllPrivileges}>
                    Select All
                  </SectionSelectBtn>
                  <SectionSelectBtn type="button" onClick={deselectAllPrivileges}>
                    Deselect All
                  </SectionSelectBtn>
                </GlobalActions>
              </PrivilegesHeader>
              <SectionGrid>
                <OtherPrivilegesCard>
                  <OtherPrivilegesTitle>Other permissions</OtherPrivilegesTitle>
                  <OtherPrivilegesList>
                    {STANDALONE_PRIVILEGES.map((s) => (
                      <CheckboxRow key={s.key}>
                        <Checkbox
                          type="checkbox"
                          checked={form.privileges.includes(s.key)}
                          onChange={() => togglePrivilege(s.key)}
                        />
                        <span>{s.label}</span>
                      </CheckboxRow>
                    ))}
                  </OtherPrivilegesList>
                </OtherPrivilegesCard>
                {PORTAL_SECTIONS.map((section) => {
                  const selected = isSectionSelected(section.id);
                  return (
                    <SectionCard key={section.id} selected={selected}>
                      <SectionCardHeader
                        selected={selected}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => toggleSection(section.id, e.target.checked)}
                        />
                        <span>{section.label}</span>
                      </SectionCardHeader>
                      {selected && (
                        <CrudRow>
                          {['read', 'create', 'update', 'delete'].map((action) => {
                            const isRead = action === 'read';
                            const checked = hasSectionPrivilege(section.id, action);
                            return (
                              <CheckboxRow key={action}>
                                <Checkbox
                                  type="checkbox"
                                  checked={isRead ? true : checked}
                                  disabled={isRead}
                                  onChange={() => !isRead && toggleSectionCrud(section.id, action)}
                                />
                                <span>{CRUD_LABELS[action]}</span>
                              </CheckboxRow>
                            );
                          })}
                        </CrudRow>
                      )}
                    </SectionCard>
                  );
                })}
              </SectionGrid>
            </PrivilegesSection>
            <ModalActions>
              <CancelButton type="button" onClick={() => setShowModal(false)} disabled={saving}>
                Cancel
              </CancelButton>
              {(editing ? canUpdateStaff : canCreateStaff) && (
                <SaveButton type="button" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
                </SaveButton>
              )}
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {deleteConfirm.show && (
        <CustomConfirm
          title="Delete position?"
          message={`Delete "${deleteConfirm.position?.name}"? This will fail if any user is assigned to this position.`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm({ show: false, position: null })}
        />
      )}
    </Container>
  );
};

export default Positions;
